import React from 'react';
import styles from './LineDetails.styles';
import { ILineDetailsHandle, ILineDetailsProperties } from './LineDetails.properties';
import { withStyles } from '@material-ui/core';
import FormView from 'components/FormView';
import PurchaseOrderLineDetailsForm from './LineDetails.form';
import SummaryTable from 'components/common/SummaryTable';
import SummaryTableSchema from './LineDetails.summaryTable.schema';
import { ActionBarContext } from 'utils/ActionBarContextProvider';
import { Cancel as CancelIcon, CheckCircle } from '@markinson/uicomponents-v2/SvgIcons/';
import { useCalculateLine, useRetrievePurchaseOrdersLineDetails, useRetrievePurchaseOrdersSummary, useSetPurchaseOrdersLineDetailsQueryData, useUpdatePurchaseOrdersLine } from 'api/purchaseOrders/purchaseOrders';
import { usePOContextSelector } from '../PurchaseOrders.context';
import { useAsyncClickHandler } from 'utils/hooks';
import { showSnackBar } from 'components/common/SnackBars/SnackBar.hooks';
import { PurchaseOrdersCalculationFields } from '../PurchaseOrders.constants';

const LineDetails = (props: ILineDetailsProperties, ref: React.Ref<ILineDetailsHandle>) => {
    const { classes, formValues, changeSelectedTab, dirty, destoryForm, resetForm, selectedRecord, changeFieldValue } = props;
    const [internalDirty, setInternalDirty] = React.useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const isDirty = internalDirty || dirty;
    const onCancelClicked = React.useRef(false);
    const params = new URLSearchParams(location.search);
    const PurchaseOrder = usePOContextSelector<'PurchaseOrder'>((state) => state.PurchaseOrder) || params.get('PurchaseOrder');
    const LineNumber = usePOContextSelector<'LineNumber'>((state) => state.LineNumber) || Number(params.get('LineNumber'));
    const { data: purchaseOrdersLineDetailsData, isLoading: purchaseOrdersLineDetailsLoading, isFetching: isPurchaseOrdersLineDetailsFetching } = useRetrievePurchaseOrdersLineDetails(PurchaseOrder, LineNumber);
    const { mutateAsync: updateLineMutateAsync } = useUpdatePurchaseOrdersLine();
    const calculateLineMutation = useCalculateLine();
    const setPurchaseOrdersLineDetailsQueryData = useSetPurchaseOrdersLineDetailsQueryData();
    const fetchPOSummary = useRetrievePurchaseOrdersSummary(usePOContextSelector<'PurchaseOrder'>((state) => state.PurchaseOrder));

    const { setActionBar } = React.useContext(ActionBarContext);

    const ok = React.useCallback(
        async (data?: any) => {
            const response = await updateLineMutateAsync({
                PurchaseOrder,
                LineNumber,
                PurchaseOrderLineDetails: {
                    ...purchaseOrdersLineDetailsData?.PurchaseOrderLineDetails?.inlineObject,
                    ...formValues,
                    ...data
                },
            });

            if (response?.Status === true) {
                setPurchaseOrdersLineDetailsQueryData(PurchaseOrder, LineNumber, {
                    Status: true,
                    PurchaseOrderLineDetails: {}
                });
                destoryForm();
                setInternalDirty(false);
                changeSelectedTab('Lines');
                fetchPOSummary.refetch();
            }

            return response;
        },
        [PurchaseOrder, formValues, changeSelectedTab, purchaseOrdersLineDetailsData?.PurchaseOrderLineDetails?.inlineObject]
    );

    const { onClick: onOk, resetAsyncClick: resetOnOk } = useAsyncClickHandler(ok, !isLoading);

    const onCancel = React.useCallback(
        (): void => {
            setPurchaseOrdersLineDetailsQueryData(PurchaseOrder, LineNumber, {
                Status: true,
                PurchaseOrderLineDetails: {}
            });
            onCancelClicked.current = true;
            destoryForm();
            setInternalDirty(false);
            changeSelectedTab('Lines');

        },
        [changeSelectedTab, onCancelClicked]
    );

    const rightIcons = React.useMemo(
        () => [
            {
                label: 'Cancel',
                Icon: CancelIcon,
                iconStyle: { fill: 'rgba(178, 0, 0, 1)' },
                action: 'PurchaseordersLineDetailsCancel',
                invokeMouseDownCallback: true,
                callBack: onCancel
            },
            {
                label: 'OK',
                Icon: CheckCircle,
                action: 'PurchaseordersLineDetailsOk',
                callBack: onOk,
            },
        ],
        [onOk, onCancel]
    );

    React.useEffect(
        () => {
            setIsLoading(calculateLineMutation.isLoading);
        },
        [calculateLineMutation.isLoading]
    );

    React.useEffect(
        () => {
            setActionBar({
                leftIcons: [],
                centerIcons: [],
                rightIcons
            });
        },
        []
    );

    const calculateLineDetail = React.useCallback(
        async (name, value) => {
            if (!LineNumber || !PurchaseOrder || purchaseOrdersLineDetailsLoading ||
                purchaseOrdersLineDetailsData?.PurchaseOrderLineDetails?.inlineObject?.[name] === value) {
                return null;
            }
            const response = await calculateLineMutation.mutateAsync({
                ...purchaseOrdersLineDetailsData?.PurchaseOrderLineDetails?.inlineObject,
                ...formValues,
                PurchaseOrder,
                LineNumber,
                [name]: value,
                urlQueryParams: {
                    ChangedField: name
                }
            });

            if (response?.Status) {
                setPurchaseOrdersLineDetailsQueryData(PurchaseOrder, LineNumber, {
                    Status: true,
                    PurchaseOrderLineDetails: response?.PurchaseOrderLineDetails
                });
                setInternalDirty(true);
            } else {
                resetOnOk();
                resetForm();
            }

            return response;
        },
        [formValues, calculateLineMutation, LineNumber, PurchaseOrder, purchaseOrdersLineDetailsData?.PurchaseOrderLineDetails?.inlineObject, purchaseOrdersLineDetailsLoading]
    );

    const formViewOnBlur = React.useCallback(
        async (e: any): Promise<void> => {
            const fieldName = e?.target?.name;
            const fieldValue = e?.target?.value;

            if (onCancelClicked.current) {
                onCancelClicked.current = false;

                return;
            }

            if (fieldName) {

                if (!PurchaseOrdersCalculationFields.includes(fieldName)) {
                    setIsLoading(false);

                    return;
                }

                const isValueChanged = PurchaseOrdersCalculationFields.includes(fieldName) ? (parseFloat(fieldValue) ? (parseFloat(fieldValue) !== parseFloat(purchaseOrdersLineDetailsData?.PurchaseOrderLineDetails?.inlineObject[fieldName])) : parseFloat(purchaseOrdersLineDetailsData?.PurchaseOrderLineDetails?.inlineObject[fieldName]) !== 0) : (fieldValue !== purchaseOrdersLineDetailsData?.PurchaseOrderLineDetails?.inlineObject[fieldName]);

                const isInvalidQuantity = PurchaseOrdersCalculationFields.includes(fieldName) && (fieldValue === null || fieldValue === '');

                if (isValueChanged) {
                    setInternalDirty(true);
                    if (isInvalidQuantity) {
                        await calculateLineDetail(fieldName, 0);
                    } else {
                        await calculateLineDetail(fieldName, fieldValue);
                    }
                }
            }
        },
        [calculateLineDetail, purchaseOrdersLineDetailsData?.PurchaseOrderLineDetails?.inlineObject]
    );

    React.useImperativeHandle(
        ref,
        () => ({
            isChangesSavedOrDiscarded(): boolean {
                if (isDirty) {
                    showSnackBar({ variant: 'warning', message: 'Please save or discard changes.' });
                }

                return !isDirty;
            },
            onOk,
            onCancel
        }),
        [isDirty, onOk, onCancel, formValues]
    );

    async function onLookupValueChanged(fieldValue: any, fieldName: string): Promise<void> {
        if (fieldValue && fieldValue.Code) {
            if (fieldValue.Code.toUpperCase() !== purchaseOrdersLineDetailsData?.PurchaseOrderLineDetails?.inlineObject?.[fieldName].toUpperCase()) {
                await calculateLineDetail(fieldName, fieldValue.Code);
            }
        }
    }

    return (
        <div className={classes.formContainer}>
            <FormView
                isLoading={purchaseOrdersLineDetailsLoading || isPurchaseOrdersLineDetailsFetching}
                supplierId={Number(selectedRecord?.Supplier)}
                onBlur={formViewOnBlur}
                formName={'PurchaseOrderLineDetailsForm'}
                schema={PurchaseOrderLineDetailsForm}
                includeTabsHeight={false}
                initialValues={purchaseOrdersLineDetailsData?.PurchaseOrderLineDetails?.inlineObject}
                valuesSchema={purchaseOrdersLineDetailsData?.PurchaseOrderLineDetails?.schema}
                operationMode={'EDIT'}
                summaryTableRenderer={
                    (() => <SummaryTable
                        loading={false}
                        selectedPurchaseOrderLine={purchaseOrdersLineDetailsData?.PurchaseOrderLineDetails?.inlineObject}
                        data={SummaryTableSchema}
                    />)}
                fieldExtensions={{
                    BuyingUnit: {
                        onSelectedItemChange: (value: any) => (onLookupValueChanged(value, 'BuyingUnit'))
                    },
                    SupplierProduct: {
                        onSelectedItemChange: (item) => {
                            changeFieldValue('SupplierProductId', item?.Code ?? null);
                        },
                    },
                }}
            />
        </div>
    );
};

export default withStyles(styles, { index: 1 })(React.memo(React.forwardRef(LineDetails)));
